import React from "react";
import { Play, Users, Instagram, Linkedin } from "lucide-react";
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import EpisodesList from "./components/EpisodesList";

export default function Home() {
  const handlePlatformClick = (platform) => {
    // These would be replaced with actual podcast URLs
    const urls = {
      spotify: "https://open.spotify.com/show/3bwHtSUs6AGkSD3grOyeQC",
      apple: "https://podcasts.apple.com/podcast/your-podcast-id"
    };
    window.open(urls[platform], '_blank');
  };

  const handleSocialClick = (platform) => {
    // Real social media URLs for Coverage & Coffee
    const urls = {
      linkedin: "https://www.linkedin.com/company/the-coverage-coffee-podcast/",
      instagram: "https://www.instagram.com/coverageandcoffeepodcast",
      tiktok: "https://www.tiktok.com/@coverageandcoffeepodcast",
      youtube: "https://www.youtube.com/@CoverageCoffeePodcast"
    };
    window.open(urls[platform], '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FDF4D4]">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Indie+Flower&display=swap');
          .handwriting {
            font-family: 'Indie Flower', cursive;
          }
        `}
      </style>
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#003C4B]">
        {/* Banner */}
        <div>
          <img 
            src="/banner.svg"
            alt="Coverage and Coffee Banner"
            className="w-full h-auto"
          />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 py-12 text-center">
          {/* Tagline */}
          <p className="handwriting text-3xl md:text-4xl text-white/90 max-w-3xl mx-auto leading-relaxed tracking-wide">
            Not your typical insurance podcast
          </p>
        </div>
      </section>

      {/* Podcast Platforms */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex justify-center mb-16">
          {/* Spotify Card */}
          <Card
            className="group cursor-pointer border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2 bg-gradient-to-br from-green-500 to-green-600 text-white overflow-hidden rounded-2xl"
            onClick={() => handlePlatformClick('spotify')}
          >
            <CardContent className="p-8 md:p-10 relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full transform translate-x-8 -translate-y-8" />
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                    <Play className="w-8 h-8 text-green-500 ml-1" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Spotify</h3>
                    <p className="text-green-100">Listen on Spotify</p>
                  </div>
                </div>
                <p className="text-green-50 mb-6">
                  Stream all episodes on the world's largest music platform
                </p>
                <Button
                  variant="secondary"
                  className="bg-white text-green-600 hover:bg-green-50 font-semibold px-8 py-3 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300 w-full"
                >
                  Listen Now
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Apple Podcasts Card - Hidden for now */}
          {/* <Card
            className="group cursor-pointer border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2 bg-gradient-to-br from-purple-500 to-pink-500 text-white overflow-hidden"
            onClick={() => handlePlatformClick('apple')}
          >
            <CardContent className="p-8 md:p-10 relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full transform translate-x-8 -translate-y-8" />
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <Play className="w-4 h-4 text-white ml-0.5" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Apple Podcasts</h3>
                    <p className="text-purple-100">Listen on Apple</p>
                  </div>
                </div>
                <p className="text-purple-50 mb-6">
                  Subscribe and download episodes on Apple Podcasts
                </p>
                <Button
                  variant="secondary"
                  className="bg-white text-purple-600 hover:bg-purple-50 font-semibold px-6 py-3 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300"
                >
                  Subscribe
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card> */}
        </div>

        {/* Social Media Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {/* LinkedIn */}
          <Card
            className="group cursor-pointer border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white rounded-2xl"
            onClick={() => handleSocialClick('linkedin')}
          >
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                <Linkedin className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold text-slate-800 mb-1">LinkedIn</h4>
              <p className="text-sm text-slate-500">Professional updates</p>
            </CardContent>
          </Card>

          {/* Instagram */}
          <Card
            className="group cursor-pointer border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white rounded-2xl"
            onClick={() => handleSocialClick('instagram')}
          >
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-pink-200 transition-colors duration-300">
                <Instagram className="w-6 h-6 text-pink-600" />
              </div>
              <h4 className="font-semibold text-slate-800 mb-1">Instagram</h4>
              <p className="text-sm text-slate-500">Behind the scenes</p>
            </CardContent>
          </Card>

          {/* TikTok */}
          <Card
            className="group cursor-pointer border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white rounded-2xl"
            onClick={() => handleSocialClick('tiktok')}
          >
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-slate-200 transition-colors duration-300">
                <svg className="w-6 h-6 text-slate-800" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.53.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-2.43.05-4.85-.38-6.75-1.9-1.44-1.14-2.3-2.85-2.4-4.56-.12-2.14.73-4.27 2.4-5.77 1.66-1.5 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.02.71.15 1.42.46 2.08.38.78 1.05 1.42 1.88 1.73 1.16.42 2.54.31 3.57-.29.81-.48 1.44-1.21 1.76-2.09.21-.57.27-1.2.27-1.81V.02Z"/>
                </svg>
              </div>
              <h4 className="font-semibold text-slate-800 mb-1">TikTok</h4>
              <p className="text-sm text-slate-500">Quick clips</p>
            </CardContent>
          </Card>

          {/* YouTube */}
          <Card
            className="group cursor-pointer border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white rounded-2xl"
            onClick={() => handleSocialClick('youtube')}
          >
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-red-200 transition-colors duration-300">
                <svg className="w-6 h-6 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <h4 className="font-semibold text-slate-800 mb-1">YouTube</h4>
              <p className="text-sm text-slate-500">Video content</p>
            </CardContent>
          </Card>
        </div>

        {/* Episodes Section */}
        <EpisodesList />
      </section>

      {/* Footer */}
      <footer className="bg-[#fcf5e3] border-t border-yellow-800/20">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <div className="mb-6">
            <Users className="w-8 h-8 text-yellow-800 mx-auto mb-3" />
            <h3 className="text-xl font-semibold text-slate-800 mb-2">Meet the Team</h3>
            <p className="text-slate-600 mb-6">
              Learn more about the people behind Coverage & Coffee
            </p>
          </div>
          
          <Button
            variant="outline"
            className="border-2 border-yellow-800/30 hover:bg-yellow-800/10 hover:border-yellow-800/40 font-semibold px-10 py-3 rounded-xl transition-all duration-300 text-slate-800"
            onClick={() => window.open('https://parasolplatforms.com', '_blank')}
          >
            Learn More
          </Button>
          
          <div className="mt-8 pt-8 border-t border-yellow-800/20">
            <p className="text-sm text-slate-500">
              © 2025 Coverage & Coffee. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}